import type { ReactNode } from "react";
import "./test-filter-note.css";

// ---- One voice for the global test filter (2026-08-07, audit S-02/S-11/S-22) ----
// The filter "Skrýt testovací klienty" sits once at the top of Servis and then
// silently trims every list below it. Before this, three surfaces said how much
// they hid (each in its own words), four said nothing, and the way back existed
// on exactly one of them. A list that hides rows without saying so turns every
// count above it into a lie the reader cannot check.
//
// The law: wherever the test filter removes a row, the panel says WHAT is on
// screen, WHAT is missing and offers the ONE way back, in the same sentence
// everywhere. When nothing is hidden the note stays silent - the list is then
// the whole truth and a permanent counter would only add chrome.

/**
 * The mark a visible dev seed wears. Counterpart to the note above: the note
 * speaks when rows are HIDDEN, the badge speaks when they are SHOWN. One
 * wording, one look, on every surface that can hold a test row (audit P-12).
 */
export function TestRowBadge(props: { t: (key: string) => string }) {
  return (
    <span className="test-row-badge" data-test-row="true">
      {props.t("staffConsole.testRowBadge")}
    </span>
  );
}

/**
 * A client's name and the marks that stand beside it, on every row that can
 * carry one. The name is the ONLY part allowed to ellipsize.
 *
 * P-04's fix gave the KLIENT column a fixed width and hung the ellipsis on the
 * `<strong>` that also holds the badge, so a name too long for the column
 * pushed TESTOVACÍ past the cut and the mark disappeared - on the one row
 * where the partner most needs it (audit2 N01: measured 206 px of box against
 * 484 px of content, badge starting at 400). A truncating box may hold text
 * and nothing else; anything that must stay legible is its SIBLING, at its own
 * width.
 */
export function ClientNameLine(props: {
  name: string;
  /** Optional because the row types carry it optionally; undefined is "not a seed". */
  isTest: boolean | undefined;
  t: (key: string) => string;
  /** Marks that belong to this row and are not the name (e.g. an address). */
  children?: ReactNode;
}) {
  return (
    <strong className="client-name-line" title={props.name}>
      <span className="client-name-text">{props.name}</span>
      {props.isTest && <TestRowBadge t={props.t} />}
      {props.children}
    </strong>
  );
}

export function TestFilterNote(props: {
  /** Rows the reader can see right now, after every filter. */
  shown: number;
  /** Rows the global test filter removed. Nothing renders when this is 0. */
  hidden: number;
  t: (key: string) => string;
  /** Omitted where the surface cannot reach the filter; the note still counts. */
  onShowTest?: () => void;
}) {
  if (props.hidden <= 0) return null;
  return (
    <div className="test-filter-note" role="status">
      <p className="admin-tool-hint">
        {props.t("staffConsole.testFilter.summary")
          .replace("{shown}", String(Math.max(props.shown, 0)))
          .replace("{hidden}", String(props.hidden))}
      </p>
      {props.onShowTest && (
        <button className="secondary-button compact-button" type="button" onClick={props.onShowTest}>
          {props.t("staffConsole.testFilter.showAll")}
        </button>
      )}
    </div>
  );
}
